import { Link } from "@/components/site/LocaleLink";
import { ArrowUpRight } from "lucide-react";
import { useLang, useL } from "@/context/LanguageContext";
import { CATEGORIES } from "@/data/products";
import { IMG } from "@/data/images";
import { Picture } from "@/components/site/Picture";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";

/**
 * One photographic tile. `compact` is the smaller second-row treatment — same
 * anatomy, tighter padding and type.
 */
// Every tile sits in the same two-column grid, so they all render at roughly
// half the viewport regardless of which row they are on.
const TILE_SIZES = "(min-width: 1024px) 50vw, 100vw";

const Tile = ({ to, testId, image, title, tagline, index, height, compact = false }) => (
  <RevealItem>
    <Link
      to={to}
      data-testid={testId}
      className={`group relative block overflow-hidden rounded-2xl border border-line ${height}`}
    >
      <Picture
        img={image}
        alt={title}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        sizes={TILE_SIZES}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/90 via-forest-deep/25 to-transparent" />
      <span className="absolute top-5 start-5 font-display text-sm font-bold text-cream/60">
        0{index + 1}
      </span>
      <div className={`absolute inset-x-0 bottom-0 ${compact ? "p-5 lg:p-6" : "p-6 lg:p-8"}`}>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h3
              className={`font-display font-bold text-cream ${
                compact ? "text-xl" : "text-2xl lg:text-3xl"
              }`}
            >
              {title}
            </h3>
            <p className="mt-2 max-w-md text-sm text-cream/80">{tagline}</p>
          </div>
          <span
            className={`flex shrink-0 items-center justify-center rounded-full bg-gold text-forest-deep transition-transform duration-300 group-hover:-translate-y-1 group-hover:rotate-12 ${
              compact ? "h-9 w-9" : "h-11 w-11"
            }`}
          >
            <ArrowUpRight className={`rtl-flip ${compact ? "h-4 w-4" : "h-5 w-5"}`} />
          </span>
        </div>
      </div>
    </Link>
  </RevealItem>
);

export const Categories = () => {
  const { t } = useLang();
  const L = useL();

  // The product categories carry studio product shots in products.js, which
  // read as packaging rather than as a category. The tiles use flock and field
  // photography instead — the product shots still lead each product page.
  const tiles = [
    {
      to: `/products?category=${CATEGORIES[0].id}`,
      testId: `category-card-${CATEGORIES[0].id}`,
      image: IMG.vaccinesLineup,
      title: L(CATEGORIES[0].name),
      tagline: L(CATEGORIES[0].tagline),
      height: "h-[360px] lg:h-[520px]",
    },
    {
      to: `/products?category=${CATEGORIES[1].id}`,
      testId: `category-card-${CATEGORIES[1].id}`,
      image: IMG.feedLineup,
      title: L(CATEGORIES[1].name),
      tagline: L(CATEGORIES[1].tagline),
      height: "h-[360px] lg:h-[520px]",
    },
    {
      to: "/support",
      testId: "category-card-support",
      image: IMG.healthyFlock,
      title: t.categories.support.title,
      tagline: t.categories.support.tagline,
      height: "h-[260px] lg:h-[320px]",
      compact: true,
    },
    {
      to: "/about",
      testId: "category-card-reach",
      image: IMG.flockScale,
      title: t.categories.reach.title,
      tagline: t.categories.reach.tagline,
      height: "h-[260px] lg:h-[320px]",
      compact: true,
    },
  ];

  return (
    <section className="bg-cream py-24 lg:py-32" data-testid="categories">
      <div className="container-x">
        <div className="mb-16 max-w-3xl">
          <Reveal>
            <p className="eyebrow mb-4 text-gold-ink">{t.categories.kicker}</p>
          </Reveal>
          <Reveal i={1}>
            <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-forest sm:text-5xl">
              {t.categories.title}
            </h2>
          </Reveal>
          <Reveal i={2}>
            <p className="mt-6 text-base leading-relaxed text-stone sm:text-lg">{t.categories.sub}</p>
          </Reveal>
        </div>

        <RevealGroup className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {tiles.map((tile, i) => (
            <Tile key={tile.testId} {...tile} index={i} />
          ))}
        </RevealGroup>
      </div>
    </section>
  );
};
